import * as React from "react";
import {useEffect, useState} from "react";
import {withVerifyAccess} from "../../common/withVerifyAccess";
import {UserTypeEnum} from "../../../model/UserInfo.model";
import {AnalyticsTab, DateRangePreset} from "./shared/analytics.constants";
import {getPresetRange, todayStr} from "./shared/analytics.format";
import {DateRangeFilter} from "./shared/DateRangeFilter.component";
import {clearAnalyticsCache} from "./hooks/useAnalyticsTabData";
import {clearDrilldownCache} from "./shared/drilldownCache";
import {LoginTab} from "./login/LoginTab.component";
import {EngagementTab} from "./engagement/EngagementTab.component";
import {PlansTab} from "./plans/PlansTab.component";
import {HealthTab} from "./health/HealthTab.component";

export const AdminAnalyticsPage: React.FC = () => {
    const LOGIN = "login";
    const ENGAGEMENT = "engagement";
    const PLANS = "plans";
    const HEALTH = "health";
    const [preset, setPreset] = useState<DateRangePreset>("3m");
    const [appliedRange, setAppliedRange] = useState<{ startDate: string; endDate: string }>(getPresetRange("3m"));
    const [customStart, setCustomStart] = useState<string>("");
    const [customEnd, setCustomEnd] = useState<string>(todayStr());
    const [customError, setCustomError] = useState<string | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<string>("");
    const [countryOptions, setCountryOptions] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<AnalyticsTab>(LOGIN);

    const handlePresetClick = (value: Exclude<DateRangePreset, "custom">) => {
        setPreset(value);
        setCustomError(null);
        setAppliedRange(getPresetRange(value));
    };

    const handleCustomApply = () => {
        if (!customStart) {
            setCustomError("Please select a start date.");
            return;
        }
        if (!customEnd) {
            setCustomError("Please select an end date.");
            return;
        }
        if (customStart > customEnd) {
            setCustomError("Start date must be before end date.");
            return;
        }
        setCustomError(null);
        setAppliedRange({startDate: customStart, endDate: customEnd});
    };

    useEffect(() => {
        clearAnalyticsCache();
        clearDrilldownCache();
    }, [appliedRange, selectedCountry]);

    const tabClass = (tab: AnalyticsTab) =>
        `admin-analytics__tab${activeTab === tab ? " admin-analytics__tab--active" : ""}`;

    return (
        <div className="admin-analytics new-common-styles" data-testid="admin-analytics">
            <div className="admin-analytics__header">
                <h1 className="admin-analytics__title">Site Analytics</h1>
            </div>
            <DateRangeFilter
                preset={preset}
                appliedRange={appliedRange}
                customStart={customStart}
                customEnd={customEnd}
                customError={customError}
                selectedCountry={selectedCountry}
                countryOptions={countryOptions}
                setPreset={setPreset}
                setCustomStart={setCustomStart}
                setCustomEnd={setCustomEnd}
                setSelectedCountry={setSelectedCountry}
                onPresetClick={handlePresetClick}
                onCustomApply={handleCustomApply}
            />

            <nav className="admin-analytics__tabs" role="tablist" aria-label="Analytics sections">
                <button role="tab" aria-selected={activeTab === LOGIN}
                        className={tabClass(LOGIN)}
                        onClick={() => setActiveTab(LOGIN)}>Login Activity
                </button>
                <button role="tab" aria-selected={activeTab === ENGAGEMENT}
                        className={tabClass(ENGAGEMENT)}
                        onClick={() => setActiveTab(ENGAGEMENT)}>Site Engagement
                </button>
                <button role="tab" aria-selected={activeTab === PLANS}
                        className={tabClass(PLANS)}
                        onClick={() => setActiveTab(PLANS)}>Plan Analytics
                </button>
                <button role="tab" aria-selected={activeTab === HEALTH}
                        className={tabClass(HEALTH)}
                        onClick={() => setActiveTab(HEALTH)}>User Health
                </button>
            </nav>

            {activeTab === LOGIN && (
                <LoginTab
                    range={appliedRange}
                    country={selectedCountry}
                    onCountryOptions={setCountryOptions}
                />
            )}

            {activeTab === ENGAGEMENT && (
                <EngagementTab range={appliedRange} country={selectedCountry}/>
            )}

            {activeTab === PLANS && (
                <PlansTab range={appliedRange} country={selectedCountry}/>
            )}

            {activeTab === HEALTH && (
                <HealthTab range={appliedRange} country={selectedCountry}/>
            )}
        </div>
    );
};

export default withVerifyAccess(AdminAnalyticsPage, UserTypeEnum.ADMIN);
